import { useMemo } from 'react'
import { collection, orderBy, query } from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { useFirestoreCollection } from './useFirestore'
import { useAuth } from './useAuth'
import { useAdminPendingDocs } from './useAdminPendingDocs'
import type { EventSuggestion } from '../types'

const EMPTY: EventSuggestion[] = []

// Every user-submitted suggestion, newest first. Admin-only: rules let a user
// read their own suggestions, but this subscribes to the whole collection, so
// non-admins run no query at all rather than hitting a permission error.
export function useAdminEventSuggestions() {
  const { isAdmin } = useAuth()

  const suggestionsQuery = useMemo(
    () =>
      isAdmin
        ? (query(
            collection(db, 'eventSuggestions'),
            orderBy('createdAt', 'desc'),
          ) as Query<EventSuggestion>)
        : null,
    [isAdmin],
  )

  const { data, loading, error } = useFirestoreCollection<EventSuggestion>(suggestionsQuery)

  return { suggestions: isAdmin ? data : EMPTY, loading, error }
}

// Suggestions the seeding routine still has to work through. A 'pending' doc
// that already carries eventIds was seeded and is only waiting on fulfillment
// (or on an admin publishing its draft events, which the draft-events badge
// already counts) — counting it here would double-count the same work.
export function useAdminPendingSuggestionCount(): number {
  const pending = useAdminPendingDocs<EventSuggestion>('eventSuggestions')
  return useMemo(() => pending.filter(s => (s.eventIds?.length ?? 0) === 0).length, [pending])
}
